/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

import {ApiError, Checkout} from "@connox/typescript-library";
import braintree, {BraintreeError, Client} from "braintree-web";
import HostedFields, {HostedFieldFieldOptions} from "braintree-web/hosted-fields";
import ThreeDSecure from "braintree-web/three-d-secure";
import React, {useState} from "react";
import Spinner from "../../utils/loader/Spinner";
import {Button, ButtonStyles, ButtonType} from "../form/Button/Button";
import {IPaymentModule, IPurchaseButtonProps, ISubmitPaymentResponse} from "./IPaymentModule";
import {INTERNAL_ERROR_TYPE, PaymentController, Payments} from "./PaymentController";
import styles from "../../checkout/steps/Payment/CreditCardContainer.module.scss";
import window from "../WindowExtended";
import i18n from "i18next";

export default class CreditCardModule implements IPaymentModule {
  public readonly checkout: Checkout;
  private client?: Client;
  private instance?: HostedFields;
  private threeDSecure: ThreeDSecure;
  private threeDSecureParameters?: IThreeDSecureParams;
  private paymentSelected: boolean;
  private supportedCardTypes: string[] = [];

  constructor(checkout: Checkout) {
    this.checkout = checkout;
    this.paymentSelected = true;
    this.PurchaseButton = this.PurchaseButton.bind(this) as (props: IPurchaseButtonProps) => React.ReactElement;
  }

  public PurchaseButton(props: IPurchaseButtonProps): React.ReactElement {
    const [visible, setVisible] = useState<boolean>(true);
    const {gotoHandling, handleError, label} = props;

    const actionMethod = async () => {
      if (!this.threeDSecureParameters) {
        gotoHandling();

        return;
      }
      setVisible(false);
      try {
        if (window.annotationTextArea !== (this.checkout.customerRemark ?? undefined)) {
          await this.checkout.put({customerRemark: window.annotationTextArea});
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        await PaymentController.initPayment();

        this.threeDSecure.on("customer-canceled", async () => {
          this.threeDSecureParameters = undefined;
          await this.checkout.put({paymentId: null});
        });

        const verifyCardResponse = await this.threeDSecure.verifyCard(this.threeDSecureParameters);
        if (!verifyCardResponse.liabilityShifted) {
          this.threeDSecureParameters = undefined;

          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
        await PaymentController.authorizePayment(verifyCardResponse.nonce);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await this.checkout.purchase();
      } catch (err) {
        const tempError = ((err as ApiError).originalError ?? err) as Error;
        tempError.name = INTERNAL_ERROR_TYPE.CREDIT_CARD.OnCatch + tempError.name;
        this.threeDSecureParameters = undefined;
        setVisible(true);
        handleError(tempError, "Credit card purchase error");
      }
    };

    const spinner = React.createElement(Spinner, {}, null);

    return React.createElement(
      Button,
      {action: actionMethod, styles: [ButtonStyles.wide], type: visible ? ButtonType.default : ButtonType.disabled},
      visible ? label : spinner,
    );
  }

  public setAsDeselected(): void {
    this.paymentSelected = false;
  }

  public async onEnterSummary(): Promise<void> {
    return Promise.resolve();
  }

  public isDataComplete(): boolean {
    return !!this.threeDSecureParameters;
  }

  public async onPaymentSelect(cardImageRef: React.MutableRefObject<HTMLDivElement | null>): Promise<void> {
    const {client, token} = await PaymentController.initBraintreeClient();
    this.client = client;
    if (!this.paymentSelected) {
      return;
    }

    this.instance = await braintree.hostedFields.create(this.setupOptions());
    this.supportedCardTypes = await this.instance.getSupportedCardTypes();
    this.instance.on("cardTypeChange", (event: {cards: {type: string; niceType: string}[]}) => {
      if (cardImageRef.current) {
        if (event.cards.length === 1 && this.supportedCardTypes.includes(event.cards[0].niceType)) {
          cardImageRef.current.className = `${styles.cardImage} ${styles[event.cards[0].type]}`;
        } else {
          cardImageRef.current.className = styles.cardImage;
        }
      }
    });
    this.threeDSecure = await braintree.threeDSecure.create({client: this.client, authorization: token, version: 2});
  }

  public async onSubmitPayment(): Promise<ISubmitPaymentResponse> {
    /* eslint-disable no-underscore-dangle */
    if (!this.instance._state.cards.length) {
      return {success: false, err: {code: "HOSTED_FIELDS_FIELDS_INVALID", cardType: ""}};
    }
    const currentCard = this.instance._state.cards[0].niceType;
    /* eslint-enable no-underscore-dangle */
    if (!this.supportedCardTypes.includes(currentCard)) {
      return {success: false, err: {code: "CARD_TYPE_NOT_SUPPORTED", cardType: currentCard}};
    }
    const tokenizeReturn = await this.instance.tokenize();
    const {details, nonce} = tokenizeReturn;
    const {bin} = details;
    const {cartData, deliveryAddress, invoiceAddress} = this.checkout;
    const {amount} = cartData.value.payable;
    if (!invoiceAddress || !deliveryAddress) {
      throw new Error("Shipping/Billing line1 format is invalid.");
    }

    this.threeDSecureParameters = {
      addFrame: null,
      additionalInformation: {
        shippingAddress: {
          countryCodeAlpha2: deliveryAddress.countryCode,
          extendedAddress: "",
          locality: deliveryAddress.city,
          postalCode: deliveryAddress.zip,
          region: deliveryAddress.state || "",
          streetAddress: ["GB", "FR", "US"].includes(deliveryAddress.countryCode)
            ? `${deliveryAddress.number} ${deliveryAddress.street}`
            : `${deliveryAddress.street} ${deliveryAddress.number}`,
        },
        shippingGivenName: deliveryAddress.firstName,
        shippingPhone: deliveryAddress.phone || "",
        shippingSurname: deliveryAddress.lastName,
        workPhoneNumber: "",
      },
      amount: parseFloat(amount),
      billingAddress: {
        countryCodeAlpha2: invoiceAddress.countryCode,
        extendedAddress: "",
        givenName: invoiceAddress.firstName,
        locality: invoiceAddress.city,
        phoneNumber: invoiceAddress.phone || "",
        postalCode: invoiceAddress.zip,
        region: invoiceAddress.state || "",
        streetAddress: ["GB", "FR", "US"].includes(invoiceAddress.countryCode)
          ? `${invoiceAddress.number} ${invoiceAddress.street}`
          : `${invoiceAddress.street} ${invoiceAddress.number}`,
        surname: invoiceAddress.lastName,
      },
      bin,
      email: invoiceAddress.email || "",
      nonce,
      onLookupComplete: (data, next) => next(),
    };

    return {success: true};
  }

  private setupOptions = (): IHostedFieldsOptions => {
    if (!this.client) {
      throw new Error("The braintree client has to be initialized first");
    }

    return {
      client: this.client,
      fields: {
        cvv: {
          placeholder: i18n.t("CVV", {ns: "checkout"}),
          selector: "#cvv",
        },
        expirationDate: {
          placeholder: i18n.t("MM/JJJJ", {ns: "checkout"}),
          selector: "#expiration-date",
        },
        number: {
          placeholder: i18n.t("Bitte Kreditkartennummer eingeben", {ns: "checkout"}),
          selector: "#card-number",
        },
      },
      styles: {
        "input": {
          "font-size": "16px",
        },
        "input.invalid": {
          color: "red",
        },
        "input.valid": {
          color: "green",
        },
      },
    };
  };
}

// if sth is missing, have a look into 'node_modules/braintree-web/three-d-secure/external/three-d-secure.js:396'
interface IThreeDSecureParams {
  addFrame: ((err?: BraintreeError, iframe?: HTMLIFrameElement) => void) | null;
  additionalInformation?: {
    shippingGivenName: string;
    shippingSurname: string;
    shippingPhone: string;
    shippingAddress: {
      streetAddress: string;
      extendedAddress: string;
      locality: string;
      region: string;
      postalCode: string;
      countryCodeAlpha2: string;
    };
    workPhoneNumber: string;
  };
  amount: number;
  bin: string;
  billingAddress: {
    givenName: string;
    surname: string;
    phoneNumber: string;
    streetAddress: string;
    extendedAddress: string;
    locality: string;
    region: string;
    postalCode: string;
    countryCodeAlpha2: string;
  };
  email: string;
  nonce: string;
  onLookupComplete: (data, next: () => void) => void;
}

export interface IHostedFieldsOptions {
  client: Client;
  fields: HostedFieldFieldOptions;
  styles: any;
}
